import { Box } from "@mui/material";
import { type FC, memo, useMemo } from "react";
import type { DayItem } from "../../../utils/models";
import type { FlatRow } from "./FlatRowRenderer";
import { RoleSectionHeader } from "./RoleSectionHeader";
import { TimeTitleRow } from "./TimeTitleRow";

type Props = {
	flatRows: FlatRow[];
	startIndex: number;
	weekViewData: DayItem[];
	roleGroups?: Record<string, string[]>;
	onToggleTitle: (key: string) => void;
	onCollapseRole: (titleKeys: string[]) => void;
};

const findTopHeaders = (rows: FlatRow[], startIndex: number) => {
	let roleRow: FlatRow | null = null;
	let timeRow: FlatRow | null = null;
	const ceiling = Math.min(startIndex, rows.length - 1);
	for (let i = 0; i <= ceiling; i++) {
		const row = rows[i];
		if (row.kind === "role-header") {
			roleRow = row;
			timeRow = null;
		} else if (row.kind === "time-title") {
			timeRow = row;
		}
	}
	return { roleRow, timeRow };
};

export const StickyHeaderOverlay: FC<Props> = memo(({ flatRows, startIndex, weekViewData, roleGroups, onToggleTitle, onCollapseRole }) => {
	const { roleRow, timeRow } = useMemo(() => findTopHeaders(flatRows, startIndex), [flatRows, startIndex]);

	if (!roleRow || roleRow.kind !== "role-header") return null;

	const titleKeys = roleRow.section.timeTitles.map((t) => `${roleRow.section.shortRole}__${t.label}`);

	return (
		<Box sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 3, bgcolor: "white", pointerEvents: "none" }}>
			<Box sx={{ pointerEvents: "auto" }}>
				<RoleSectionHeader
					section={roleRow.section}
					roleGroups={roleGroups}
					hasExpanded={roleRow.hasExpanded}
					onCollapseAll={() => onCollapseRole(titleKeys)}
				/>
			</Box>
			{timeRow && timeRow.kind === "time-title" && (
				<Box sx={{ pointerEvents: "auto" }}>
					<TimeTitleRow
						title={timeRow.title}
						section={timeRow.section}
						isExpanded={timeRow.isExpanded}
						onToggle={() => onToggleTitle(timeRow.titleKey)}
						weekViewData={weekViewData}
					/>
				</Box>
			)}
		</Box>
	);
});
StickyHeaderOverlay.displayName = "StickyHeaderOverlay";
